import {
  FunctionComponent,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DraftConfiguration, Point } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { pointerDownEvent } from '@/utils/pointerEvents';

const isCtrlKey = ({ key }: KeyboardEvent) => key === 'Control';

type TransformedData = [Point, boolean, boolean, boolean?]; // transformedDelta, first, last, dragging

export interface MultiSelectData {
  isModeActive: boolean;
  configurationIds: number[];
  addConfiguration: (configurationId: number) => void;
  removeConfiguration: (configurationId: number) => void;
  clearConfigurations: () => void;
  setTransformData: (data: TransformedData) => void;
  transformData: TransformedData | undefined;
}

const noop = () => {};

const MultiSelectContext = createContext<MultiSelectData>({
  isModeActive: false,
  configurationIds: [],
  addConfiguration: noop,
  removeConfiguration: noop,
  clearConfigurations: noop,
  setTransformData: noop,
  transformData: undefined,
});

interface MultiSelectProviderProps {
  children: ReactNode;
  configurations: DraftConfiguration[];
  selectedConfigurationId: number;
}

export const MultiSelectProvider: FunctionComponent<MultiSelectProviderProps> = ({
  configurations,
  selectedConfigurationId,
  children,
}) => {
  const [isModeActive, setIsModeActive] = useState(false);
  const [configurationIds, setConfigurationIds] = useState<number[]>([]);
  const [transformData, setTransformData] = useState<TransformedData | undefined>();

  const clearConfigurations = () => setConfigurationIds([]);

  useEffect(() => {
    const existingConfigurationIds = new Set(configurations.map(({ cid }) => cid));
    const cleanedConfigurationIds = configurationIds.filter((configurationId) =>
      existingConfigurationIds.has(configurationId)
    );
    if (cleanedConfigurationIds.length < configurationIds.length) {
      setConfigurationIds(cleanedConfigurationIds);
    }
  }, [configurations, configurationIds]);

  useEffect(() => {
    const setModeInactive = () => {
      document.removeEventListener('keyup', onCtrlKeyUp);
      window.removeEventListener('blur', setModeInactive);
      setIsModeActive(false);
    };
    const onCtrlKeyUp = (event: KeyboardEvent) => {
      if (isCtrlKey(event)) {
        setModeInactive();
      }
    };
    const onCtrlKeyDown = (event: KeyboardEvent) => {
      if (isCtrlKey(event)) {
        setIsModeActive(true);
        document.addEventListener('keyup', onCtrlKeyUp);
        window.addEventListener('blur', setModeInactive);
      }
    };
    document.addEventListener('keydown', onCtrlKeyDown);

    return () => {
      document.removeEventListener('keydown', onCtrlKeyDown);
      document.removeEventListener('keyup', onCtrlKeyUp);
      document.removeEventListener('blur', setModeInactive);
    };
  }, []);

  const hasActiveConfigurations = configurationIds.length > 0;
  useEffect(() => {
    if (hasActiveConfigurations) {
      const deselectConfigurations = (event: MouseEvent) => {
        const eventTargetElement = event.target as Element;

        if (
          eventTargetElement.matches(
            '.configuration-handles--multi-select *, .configurations-frame--multi-select'
          )
        ) {
          // event was triggered by one of the ignored elements) {
          return;
        }
        requestAnimationFrame(clearConfigurations);
      };
      document.addEventListener(pointerDownEvent, deselectConfigurations, true);
      return () => {
        document.removeEventListener(pointerDownEvent, deselectConfigurations, true);
      };
    }
  }, [hasActiveConfigurations]);

  const multiSelectData = useMemo<MultiSelectData>(
    () => ({
      isModeActive,
      configurationIds,
      addConfiguration: (configurationId: number) => {
        trackFeature('multiselect');
        setConfigurationIds((configurationIds) => {
          if (configurationIds.length === 0 && configurationId !== selectedConfigurationId) {
            return [selectedConfigurationId, configurationId];
          }
          return [...configurationIds, configurationId];
        });
      },
      removeConfiguration: (configurationId: number) =>
        setConfigurationIds((configurationIds) =>
          configurationIds.filter((id) => id !== configurationId)
        ),
      clearConfigurations,
      setTransformData,
      transformData,
    }),
    [configurationIds, selectedConfigurationId, isModeActive, transformData]
  );

  return (
    <MultiSelectContext.Provider value={multiSelectData}>{children}</MultiSelectContext.Provider>
  );
};

export const useMultiSelectContext = () => useContext(MultiSelectContext);
